/**
 * Smart Tooltip Positioning Hook
 *
 * Automatically adjusts tooltip position to prevent viewport overflow.
 * Ensures tooltips are always fully visible within the browser window.
 *
 * @param x - Mouse X position (fixed)
 * @param y - Mouse Y position (fixed)
 * @param estimatedHeight - Estimated tooltip height in pixels (default: 350)
 * @param estimatedWidth - Estimated tooltip width in pixels (default: 250)
 * @param horizontalOffset - Horizontal offset from cursor (default: 10)
 * @param verticalBuffer - Minimum distance from viewport edges (default: 20)
 * @param side - Preferred side ('left' | 'right' | 'auto'). 'auto' picks based on viewport bounds.
 *
 * @returns Object with positioning styles for the tooltip
 */
export interface SmartTooltipPosition {
  position: 'fixed';
  left: string;
  top: string;
  transform: string;
  pointerEvents: 'none';
  zIndex: number;
}

export interface UseSmartTooltipPositionOptions {
  estimatedHeight?: number;
  estimatedWidth?: number;
  horizontalOffset?: number;
  verticalBuffer?: number;
  side?: 'left' | 'right' | 'auto';
}

export const useSmartTooltipPosition = (
  x: number,
  y: number,
  options: UseSmartTooltipPositionOptions = {}
): SmartTooltipPosition => {
  const {
    estimatedHeight = 350,
    estimatedWidth = 250,
    horizontalOffset = 10,
    verticalBuffer = 20,
    side = 'auto'
  } = options;

  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  // Calculate if tooltip would overflow bottom of viewport
  // y is the mouse position, tooltip is centered with -50% transform by default
  const tooltipTop = y - (estimatedHeight / 2);
  const tooltipBottom = y + (estimatedHeight / 2);

  let verticalTransform = '-50%'; // Default: center at mouse

  // If tooltip would go below viewport, align bottom with mouse position
  if (tooltipBottom > viewportHeight - verticalBuffer) {
    verticalTransform = `calc(-100% + ${verticalBuffer}px)`; // Position above mouse
  }

  // If tooltip would go above viewport, align top with mouse position
  if (tooltipTop < verticalBuffer) {
    verticalTransform = `-${verticalBuffer}px`; // Position below mouse
  }

  // Determine which side to render on
  let actualSide: 'left' | 'right' = 'right';

  if (side === 'auto') {
    // Check if tooltip would overflow right side of viewport
    const wouldOverflowRight = x + horizontalOffset + estimatedWidth > viewportWidth - verticalBuffer;
    // Check if tooltip would overflow left side of viewport
    const wouldOverflowLeft = x - horizontalOffset - estimatedWidth < verticalBuffer;

    if (wouldOverflowRight && !wouldOverflowLeft) {
      actualSide = 'left';
    } else if (wouldOverflowLeft && !wouldOverflowRight) {
      actualSide = 'right';
    } else {
      // Default to right if both would overflow or neither would
      actualSide = 'right';
    }
  } else {
    actualSide = side;
  }

  // Horizontal positioning (left or right of cursor)
  const horizontalTransform = actualSide === 'left'
    ? `calc(-100% - ${horizontalOffset}px)`
    : `${horizontalOffset}px`;

  return {
    position: 'fixed',
    left: `${x}px`,
    top: `${y}px`,
    transform: `translate(${horizontalTransform}, ${verticalTransform})`,
    pointerEvents: 'none',
    zIndex: 1000
  };
};
